import type { JudgementTone } from '@/design-system';
import type {
    CostAmounts,
    CostKey,
    PublicFullReport,
    PublicReportChapter,
    PublicReportEntry,
    PublicReportHeading,
    ReportText,
} from '@/entities/publicReport';

import { chapterAnchor } from './useReportView';

/**
 * Het volledige rapport als iets om in te zoeken en naar te wijzen: elke
 * bevinding en opmerking met een eigen anker en de plek waar hij staat, de
 * tellers voor de filterbalk, en per NHG-regel de bevindingen erachter.
 *
 * Puur rekenwerk op de gegevens van de API, dus los te testen; de schermen
 * lezen alleen dit.
 */

export const FINDING_FILTERS = ['alles', 'gebreken', 'aandacht', 'kosten'] as const;

export type FindingFilter = (typeof FINDING_FILTERS)[number];

/** Een bevinding of opmerking, met waar hij in het rapport staat. */
export interface IndexedEntry {
    entry: PublicReportEntry;
    /** Het `id` in de pagina; een sprong naar de bevinding gaat hierheen. */
    anchor: string;
    chapterKey: string;
    /** De kop erboven, zoals het rapport hem noemt: "25.1 Pannen", of het hoofdstuk. */
    place: string;
    /** Het nummer van die kop ("25.1"), zoals de NHG-bijlage de plek van een gebrek noemt. */
    placeNumber: string | null;
    /** De kop erboven als plek voor een link (`25-1`), of het hoofdstuk. */
    targetKey: string;
    /** Een opmerking staat erbij, maar telt nooit als bevinding. */
    isFinding: boolean;
}

/** Een plek in het rapport waar een link heen kan: een hoofdstuk, of een kop erin. */
export interface ReportTarget {
    key: string;
    chapterKey: string;
    /** Het `id` in de pagina: `rapport-<key>`. */
    anchor: string;
    /** Bij een kop de kop zelf, om te weten of een filter hem wegzet; `null` bij een hoofdstuk. */
    heading: PublicReportHeading | null;
}

export interface FullReportIndex {
    /** In de volgorde van het rapport. */
    entries: IndexedEntry[];
    /** Alle bevindingen, zonder de opmerkingen: de `y` in "x van y bevindingen". */
    findingCount: number;
    /** Per filter hoeveel bevindingen erin vallen; `alles` is `findingCount`. */
    counts: Record<FindingFilter, number>;
    /** Bevindingen per hoofdstuk, voor de kop van een ingeklapt hoofdstuk. */
    chapterFindings: ReadonlyMap<string, number>;
    /** De bevindingen per regel van de NHG-bijlage. */
    byNhgCode: ReadonlyMap<string, IndexedEntry[]>;
    /** De regels die de bijlage echt heeft: alleen daar wijst een NHG-code naartoe. */
    nhgRows: ReadonlySet<string>;
    /** Elk hoofdstuk en elke kop op zijn `key`: waar `#rapport-6-2` heen gaat. */
    targets: ReadonlyMap<string, ReportTarget>;
    /** Om een bevinding uit de gegevens terug te vinden. */
    of: (entry: PublicReportEntry) => IndexedEntry | undefined;
}

/** Het `id` van een bevinding in de pagina. Niet `rapport-…`: dat is het anker van een hoofdstuk. */
export function entryAnchor(position: number): string {
    return `bevinding-${position}`;
}

/** Het `id` van een regel in de resultatentabellen van de NHG-bijlage. */
export function nhgRowAnchor(code: string): string {
    const slug = code
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

    return `nhg-regel-${slug === '' ? 'onbekend' : slug}`;
}

export function indexFullReport(report: PublicFullReport): FullReportIndex {
    const entries: IndexedEntry[] = [];
    const lookup = new Map<PublicReportEntry, IndexedEntry>();
    const chapterFindings = new Map<string, number>();
    const byNhgCode = new Map<string, IndexedEntry[]>();
    const nhgRows = new Set<string>();
    const targets = new Map<string, ReportTarget>();

    const add = (
        chapter: PublicReportChapter,
        entry: PublicReportEntry,
        number: string | null,
        title: string,
        targetKey: string,
    ) => {
        const indexed: IndexedEntry = {
            entry,
            anchor: entryAnchor(entries.length + 1),
            chapterKey: chapter.key,
            place: number ? `${number} ${title}` : title,
            placeNumber: number,
            targetKey,
            isFinding: !entry.remark,
        };

        entries.push(indexed);
        lookup.set(entry, indexed);

        if (indexed.isFinding) {
            chapterFindings.set(chapter.key, (chapterFindings.get(chapter.key) ?? 0) + 1);
        }

        if (entry.nhgCode !== null) {
            byNhgCode.set(entry.nhgCode, [...(byNhgCode.get(entry.nhgCode) ?? []), indexed]);
        }
    };

    for (const chapter of report.chapters) {
        if (chapter.kind === 'nhg') {
            for (const table of chapter.annex.results) {
                for (const row of table.rows) {
                    nhgRows.add(row.code);
                }
            }

            continue;
        }

        targets.set(chapter.key, {
            key: chapter.key,
            chapterKey: chapter.key,
            anchor: chapterAnchor(chapter.key),
            heading: null,
        });

        if (chapter.kind !== 'part') {
            continue;
        }

        for (const entry of chapter.entries) {
            add(chapter, entry, chapter.number, chapter.title, chapter.key);
        }

        for (const heading of chapter.headings) {
            targets.set(heading.key, {
                key: heading.key,
                chapterKey: chapter.key,
                anchor: chapterAnchor(heading.key),
                heading,
            });

            for (const entry of heading.entries) {
                add(chapter, entry, heading.number, heading.title, heading.key);
            }
        }
    }

    const findings = entries.filter((indexed) => indexed.isFinding);
    const count = (filter: FindingFilter) =>
        findings.filter((indexed) => matchesFilter(indexed, filter)).length;

    return {
        entries,
        findingCount: findings.length,
        counts: {
            alles: findings.length,
            gebreken: count('gebreken'),
            aandacht: count('aandacht'),
            kosten: count('kosten'),
        },
        chapterFindings,
        byNhgCode,
        nhgRows,
        targets,
        of: (entry) => lookup.get(entry),
    };
}

/**
 * Of een bevinding in een filter valt. Onder "Alles" staan ook de opmerkingen;
 * de andere filters gaan alleen over bevindingen.
 */
export function matchesFilter(indexed: IndexedEntry, filter: FindingFilter): boolean {
    switch (filter) {
        case 'alles':
            return true;
        case 'gebreken':
            return indexed.isFinding && indexed.entry.severity === 'gebrek';
        case 'aandacht':
            return indexed.isFinding && indexed.entry.severity === 'aandacht';
        case 'kosten':
            return indexed.isFinding && indexed.entry.costs.length > 0;
    }
}

/** Of een kop onder dit filter nog iets te tonen heeft. */
export function headingMatches(
    heading: PublicReportHeading,
    index: FullReportIndex,
    filter: FindingFilter,
): boolean {
    return heading.entries.some((entry) => entryMatches(entry, index, filter));
}

export function entryMatches(
    entry: PublicReportEntry,
    index: FullReportIndex,
    filter: FindingFilter,
): boolean {
    const indexed = index.of(entry);

    return indexed !== undefined && matchesFilter(indexed, filter);
}

/**
 * Of een hoofdstuk onder dit filter in beeld blijft. Zonder filter alles; met
 * een filter alleen de onderdelen met een bevinding die erin valt - hun koppen
 * blijven staan, zodat duidelijk is waar een bevinding zit.
 */
export function chapterVisible(
    chapter: PublicReportChapter,
    index: FullReportIndex,
    filter: FindingFilter,
): boolean {
    if (filter === 'alles') {
        return true;
    }

    if (chapter.kind !== 'part') {
        return false;
    }

    return (
        chapter.entries.some((entry) => entryMatches(entry, index, filter)) ||
        chapter.headings.some((heading) => headingMatches(heading, index, filter))
    );
}

/**
 * Welke hoofdstukken in de bijlage "Toelichting en voorwaarden" horen: de
 * voorwaarden, en de onderdelen die alleen uitleggen aan het begin en aan het
 * eind van het rapport.
 *
 * De API beslist het per hoofdstuk (`appendix`), met dezelfde regel als de PDF
 * (`ReportDocumentBuilder::split()`): alleen wat de vragenlijst zelf
 * *Informatief* noemt, alleen de reeks vooraan en achteraan, en een onderdeel met
 * een foto van de woning blijft staan. Die regel leest het oordeel als getal,
 * dat deze pagina nooit krijgt; daarom staat hij alleen daar.
 */
export function appendixKeys(report: PublicFullReport): ReadonlySet<string> {
    return new Set(
        report.chapters.filter((chapter) => chapter.appendix).map((chapter) => chapter.key),
    );
}

/**
 * Het rapport in leesvolgorde: eerst wat er in deze woning is gevonden, dan de
 * bijlage met de algemene toelichting en de voorwaarden.
 *
 * Eén keer toegepast op het volledige rapport, zodat het overzicht, de
 * kostentabel en het tabblad Rapport dezelfde volgorde aanhouden. Verandert er
 * niets aan de volgorde, dan komt hetzelfde object terug. Nog een keer toepassen
 * verandert niets meer: de verplaatste hoofdstukken vormen dan de reeks achteraan.
 */
export function inReadingOrder(report: PublicFullReport): PublicFullReport {
    const keys = appendixKeys(report);

    if (keys.size === 0) {
        return report;
    }

    const chapters = [
        ...report.chapters.filter((chapter) => !keys.has(chapter.key)),
        ...report.chapters.filter((chapter) => keys.has(chapter.key)),
    ];

    return chapters.every((chapter, index) => chapter === report.chapters[index])
        ? report
        : { ...report, chapters };
}

export interface CostBreakdownRow {
    indexed: IndexedEntry;
    amount: number;
}

export interface CostBreakdown {
    /** Duurste eerst. */
    rows: CostBreakdownRow[];
    /** Wat het blok zegt; de rijen plus `rest` tellen hier precies op uit. */
    total: number;
    /**
     * Wat niet bij een bevinding in het rapport staat. Hoort `0` te zijn; staat
     * er toch iets, dan zegt de lijst dat, zodat de optelling klopt.
     */
    rest: number;
}

/**
 * De bevindingen achter één kostenblok (Direct, Op termijn, Verbetering), met
 * hun bedrag in dat blok.
 */
export function costBreakdown(index: FullReportIndex, key: CostKey, total: number): CostBreakdown {
    const rows = index.entries
        .flatMap((indexed) => {
            const cost = indexed.entry.costs.find((item) => item.key === key);

            return cost && cost.amount !== 0 ? [{ indexed, amount: cost.amount }] : [];
        })
        .sort((a, b) => b.amount - a.amount);

    const sum = rows.reduce((carry, row) => carry + row.amount, 0);
    /* In centen, zodat 0,1 + 0,2 geen restpost van een fractie van een cent maakt. */
    const rest = (Math.round(total * 100) - Math.round(sum * 100)) / 100;

    return { rows, total, rest };
}

/** De titel van een bevinding; zonder titel de eerste woorden van de tekst. */
export function entryTitle(entry: PublicReportEntry): string {
    if (entry.title.trim() !== '') {
        return entry.title;
    }

    const words = plainText(entry.text);

    return words.length > 80 ? `${words.slice(0, 77).trimEnd()}…` : words;
}

function plainText(text: ReportText): string {
    return text
        .flatMap((block) => (block.type === 'list' ? block.items : block.lines))
        .map((line) => line.map((node) => node.text).join(''))
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
}

/** Een regel van het overzicht: een onderdeel of een kop erin, met de koppen eronder. */
export interface OutlineItem {
    /** Waar een tik heen gaat: `#rapport-<key>`. */
    key: string;
    number: string | null;
    title: string;
    /** "Matig" - het woord, nooit het getal; `null` zonder oordeel. */
    judgement: string | null;
    /** De kleur van dat oordeel, dezelfde als in het rapport. */
    tone: JudgementTone | null;
    children: OutlineItem[];
}

/**
 * Het overzicht van het rapport: elk onderdeel met de koppen eronder, genest
 * zoals het rapport ze nummert - hoofdstuk, sectie, en wat daaronder hangt.
 * Een kop die dieper staat dan de vorige hangt onder die vorige; een gat in de
 * diepte (een 4 direct onder een 2) wordt één stap, geen twee.
 */
export function reportOutline(report: PublicFullReport): OutlineItem[] {
    return report.chapters.flatMap((chapter) => {
        if (chapter.kind !== 'part') {
            return [];
        }

        const root: OutlineItem = {
            key: chapter.key,
            number: chapter.number,
            title: chapter.title,
            judgement: chapter.judgement,
            tone: chapter.tone,
            children: [],
        };
        const open: Array<{ depth: number; item: OutlineItem }> = [{ depth: 1, item: root }];

        for (const heading of chapter.headings) {
            const item: OutlineItem = {
                key: heading.key,
                number: heading.number,
                title: heading.title,
                judgement: heading.judgement,
                tone: heading.tone,
                children: [],
            };

            while (open.length > 1 && (open.at(-1)?.depth ?? 1) >= heading.depth) {
                open.pop();
            }

            (open.at(-1)?.item ?? root).children.push(item);
            open.push({ depth: heading.depth, item });
        }

        return [root];
    });
}

/** Een regel van de kostentabel: één onderdeel met zijn drie bedragen. */
export interface CostTableRow {
    key: string;
    number: string | null;
    title: string;
    amounts: CostAmounts;
}

export interface CostTable {
    /** Elk onderdeel, in de volgorde van het rapport; ook de onderdelen zonder kosten. */
    rows: CostTableRow[];
    /** Per kolom de som van de regels erboven. */
    total: CostAmounts;
}

/**
 * De kosten per onderdeel, zoals het tabblad Kosten ze toont: elk onderdeel met
 * Direct, Op termijn en Verbetering, en onderaan per kolom de som. `null` als
 * het bedrijf de kosten verbergt: dan heeft geen onderdeel bedragen.
 */
export function costTable(report: PublicFullReport): CostTable | null {
    const rows = report.chapters.flatMap((chapter) =>
        chapter.kind === 'part' && chapter.costs !== null
            ? [
                  {
                      key: chapter.key,
                      number: chapter.number,
                      title: chapter.title,
                      amounts: chapter.costs,
                  },
              ]
            : [],
    );

    if (rows.length === 0) {
        return null;
    }

    /* In centen, zodat de som precies het bedrag is dat onder de kolom hoort. */
    const sum = (key: CostKey) =>
        rows.reduce((carry, row) => carry + Math.round(row.amounts[key] * 100), 0) / 100;

    return {
        rows,
        total: { direct: sum('direct'), termijn: sum('termijn'), onderhoud: sum('onderhoud') },
    };
}
